/**
 * PWABanners — PWA prompts rendered as a stack at the bottom of the screen.
 *
 * Renders (at most one at a time, in priority order):
 *   1. SW update available   — "New version ready"
 *
 * (May 2 2026: the push permission nudge banner — "Game Reminders /
 * Get notified when your next game is coming up..." — was removed
 * per user request. Push subscription is still managed from
 * Profile → Notification Preferences for users who deliberately want
 * to opt in.)
 */
import { useState, useEffect } from 'react';
import {
  RefreshCw, X,
} from 'lucide-react';
import { usePWAContext } from '../../context/PWAContext.jsx';
import { useAppVersionPoll } from '../../hooks/useAppVersionPoll.js';

// ── Shared banner shell ───────────────────────────────────────────
function Banner({ color = 'var(--ice)', icon: Icon, title, body, actions, onDismiss, animate = true }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Tiny delay so CSS transition fires after mount
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      position: 'fixed',
      // Mobile fix: MobileLayout also mounts the fixed BottomNav tab bar,
      // whose real height is ~54px content + env(safe-area-inset-bottom)
      // (up to ~34px+ on notched/gesture-bar phones — see BottomNav.jsx).
      // A flat 80px here left this banner overlapping the nav by several
      // px on those devices. env() defaults to 0 on desktop/non-notched
      // devices, so this is a no-op there — pixel-identical elsewhere.
      bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
      left: '50%',
      transform: `translateX(-50%) translateY(${visible ? 0 : 24}px)`,
      opacity: visible ? 1 : 0,
      transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.25s ease',
      zIndex: 900,
      width: 'min(92vw, 440px)',
    }}>
      <div style={{
        background: 'rgba(10,16,24,0.97)',
        border: `1px solid ${color}35`,
        borderRadius: 16,
        boxShadow: `0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px ${color}15, 0 0 32px ${color}0a`,
        backdropFilter: 'blur(20px)',
        padding: '16px 18px',
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
      }}>
        {/* Icon orb */}
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: `${color}18`, border: `1px solid ${color}35`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginTop: 2,
        }}>
          <Icon size={18} color={color} />
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14,
            letterSpacing: '0.05em', textTransform: 'uppercase',
            color: 'var(--text)', marginBottom: 4,
          }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</div>

          {/* Action buttons */}
          {actions && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {actions.map(a => (
                <button key={a.label} onClick={a.onClick} style={{
                  padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                  background: a.primary ? `${color}20` : 'var(--surface-2)',
                  border: `1px solid ${a.primary ? `${color}50` : 'var(--border)'}`,
                  color: a.primary ? color : 'var(--text-2)',
                  fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  display: 'flex', alignItems: 'center', gap: 5,
                  transition: 'opacity 0.15s',
                }}>
                  {a.icon && <a.icon size={12} />} {a.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Dismiss */}
        {onDismiss && (
          <button onClick={onDismiss} style={{
            padding: 4, borderRadius: 6, background: 'transparent', border: 'none',
            color: 'var(--text-2)', cursor: 'pointer', flexShrink: 0, lineHeight: 0,
          }}>
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── PWABanners ─────────────────────────────────────────────────────
export function PWABanners() {
  // May 2 2026 — push-related state, effects, and handlers removed
  // along with the push-nudge banner. We no longer destructure `push`
  // from the PWA context here, and `useAuth`'s user/updateUser were
  // only needed for the pushDismissed write — gone now. The component
  // is now exclusively the SW/app-update banner.
  const { sw } = usePWAContext();
  // Apr 2026 — secondary update signal. The existing sw.updateAvailable
  // only fires when /sw.js BYTES change (someone bumped APP_VERSION in
  // sw.js). Deploys that only ship new React code but leave sw.js
  // untouched wouldn't flip it — users sat on stale bundles until they
  // manually reloaded. useAppVersionPoll fetches /version.json (emitted
  // by the Vite build) every 5 min and reload/reconnect, and flips
  // updateAvailable=true the moment the value differs from what this
  // tab booted with. The two sources compose via logical OR below:
  // whichever trips first shows the banner.
  const appVersion = useAppVersionPoll();

  // Local state for which banners have been shown/dismissed in this session
  const [swUpdateClosed,   setSwUpdateClosed]   = useState(false);

  // Unified "the frontend is out of date" flag. Either source counts.
  // Dismissed once per session via setSwUpdateClosed.
  const updateAvailable = sw.updateAvailable || appVersion.updateAvailable;
  // When the app-version poll fires (no waiting SW), we can't call
  // sw.apply — fall back to a plain reload so the browser picks up
  // the new bundle on the next navigation. When the SW path fired,
  // prefer sw.apply so the SKIP_WAITING message is sent first.
  const applyUpdate = sw.updateAvailable ? sw.apply : appVersion.apply;

  // May 2 2026 — pushDismissed read, the 20s delay effect, the
  // gameday:suggest-push event listener, dismissPushForever() and
  // handleSubscribePush() all removed along with the banner.
  // Profile → Notification Preferences still owns push subscribe/
  // unsubscribe via push.subscribe() and the `pushDismissed` flag —
  // this component is no longer involved in push state at all.

  // Priority: app/SW update only — push nudge banner is gone.
  if (updateAvailable && !swUpdateClosed) {
    return (
      <Banner
        color="var(--goal)"
        icon={RefreshCw}
        title="Update Available"
        body="A new version of Game Day is ready. Reload to get the latest."
        onDismiss={() => setSwUpdateClosed(true)}
        actions={[
          { label: 'Reload Now', primary: true, icon: RefreshCw, onClick: applyUpdate },
          { label: 'Later', onClick: () => setSwUpdateClosed(true) },
        ]}
      />
    );
  }

  // May 2 2026 — push permission nudge banner removed entirely per
  // user request. The "Get notified when your next game is coming up..."
  // popup with Enable / No Thanks buttons no longer renders. Push
  // subscription is still available through Profile → Notification
  // Preferences for users who want to opt in deliberately.

  return null;
}
